import React, {useEffect, useState} from 'react';
import moment from 'moment';
import _ from 'lodash';
import Graph from '../graph';
import {useDatabase} from '@nozbe/watermelondb/react';
import {StyleSheet, Text, View} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {Colors, Typography} from 'src/styles';

const normalizeDay = (d: any) => {
  // Represent the SAME LOCAL CALENDAR DAY (no UTC shift for YYYY-MM-DD)
  if (d == null) {
    return null;
  }
  if (typeof d === 'number') {
    return moment(d);
  }
  if (d instanceof Date) {
    return moment(d);
  }
  if (typeof d === 'string') {
    const s = d.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return moment(s, 'YYYY-MM-DD');
    }
    return moment(s); // ISO / with time/offset
  }
  return moment(d);
};

const DurationProgressGraph = ({
  sessions = [],
  program = {},
  envs = [],
  title = null,
  extras = {},
  startDate = '',
  endDate = '',
  byDay = false, // accepted but we won't pre-trim; bucketing handles it
  filteredEnvItems = [],
  shouldShowMenu = true,
}: any) => {
  const database = useDatabase();
  const [analysis, setAnalysis] = useState<any>({});

  // Inclusive date window
  const sb = normalizeDay(startDate);
  const eb = normalizeDay(endDate);
  const startBoundary = sb && sb.isValid() ? sb.clone().startOf('day') : null;
  const endBoundary = eb && eb.isValid() ? eb.clone().endOf('day') : null;
  const hasBounds = !!(startBoundary && endBoundary);
  const inRange = (ts: any) => {
    if (!hasBounds) {
      return true;
    }
    const m = normalizeDay(ts);
    if (!m || !m.isValid()) {
      return false;
    }
    return m.isSameOrAfter(startBoundary!) && m.isSameOrBefore(endBoundary!);
  };

  // Env window
  const sortedEnvs = envs.filter((env: any) => inRange(env?.date));

  // Format "X min Y sec"
  const durationTime = (seconds: number) => {
    const s = Math.max(0, Math.round(seconds));
    const min = Math.floor(s / 60);
    const sec = s % 60;
    const parts: string[] = [];
    if (min > 0) {
      parts.push(`${min} min`);
    }
    parts.push(`${sec} sec`);
    return parts.join(' ');
  };

  // Robust resolver (tries many keys; falls back to session fields)
  const resolveAnalysis = (session: any, prog: any) => {
    const A = prog?.analysis || {};
    const dayKey = normalizeDay(
      session?.sessionDate ??
        session?.session_date ??
        session?.date ??
        session?.startTimestamp,
    )?.format('YYYY-MM-DD');

    const keysToTry = [
      session?.id,
      session?.sessionId,
      session?.session_id,
      session?.sessionDate,
      session?.session_date,
      dayKey,
    ].filter(Boolean);

    for (const k of keysToTry) {
      if (A[k]) {
        return {...A[k]};
      }
    }
    if (prog?.sessionId === session?.id) {
      return {...prog};
    }

    // Fallbacks: seconds from various shapes
    const seconds =
      Number(
        session?.average ??
          session?.seconds ??
          session?.durationSeconds ??
          session?.duration_secs,
      ) || (session?.milliseconds ? Number(session.milliseconds) / 1000 : NaN);

    if (!Number.isNaN(seconds)) {
      return {
        average: Number(seconds), // seconds
        state: session?.state || '',
        collectors: session?.collectors || [],
      };
    }
    return null;
  };

  const calculateData = async () => {
    type Bucket = {
      points: Array<{
        seconds: number; // store raw seconds per row
        state: string;
        collectors?: string[];
        startTimestamp: number;
      }>;
      change: Array<{date: number; title: string; description?: string}>;
      factor: Array<{date: number; title: string; description?: string}>;
    };

    const buckets: Record<string, Bucket> = {};
    const ensure = (key: string) => {
      if (!buckets[key]) {
        buckets[key] = {points: [], change: [], factor: []};
      }
      return buckets[key];
    };

    // ✅ Do NOT pre-trim for byDay; bucket/average by calendar day
    const analyzedSessions = sessions;

    // Sessions → program points
    for (const session of analyzedSessions) {
      const m = normalizeDay(
        session?.sessionDate ??
          session?.session_date ??
          session?.date ??
          session?.startTimestamp,
      );
      if (!m || !m.isValid()) {
        continue;
      }

      const dayStart = m.clone().startOf('day');
      if (!inRange(dayStart.valueOf())) {
        continue;
      }

      const dayKey = dayStart.format('YYYY-MM-DD');
      const analysisObj = resolveAnalysis(session, program);
      if (!analysisObj) {
        continue;
      }

      // seconds expected in analysisObj.average
      const seconds = Number(analysisObj?.average) || 0;

      // Collectors → names
      let collectors: string[] = [];
      if (
        Array.isArray(analysisObj?.collectors) &&
        analysisObj.collectors.length
      ) {
        collectors = await Promise.all(
          analysisObj.collectors.map(async (collectorId: string) => {
            if (collectorId !== 'System') {
              try {
                const u = await database.get('users').find(collectorId);
                return `${u?.firstName} ${u?.lastName}`;
              } catch {
                return 'System';
              }
            }
            return 'System';
          }),
        );
      }

      ensure(dayKey).points.push({
        seconds,
        state: analysisObj?.state || session?.state || '',
        collectors,
        startTimestamp: dayStart.valueOf(),
      });
    }

    // Env items → bucket by day & type
    for (const env of sortedEnvs) {
      const m = normalizeDay(env?.date);
      if (!m || !m.isValid()) {
        continue;
      }

      const dayStart = m.clone().startOf('day');
      if (!inRange(dayStart.valueOf())) {
        continue;
      }

      const dayKey = dayStart.format('YYYY-MM-DD');
      const item = {
        date: dayStart.valueOf(),
        title: env?.title,
        description: env?.description,
      };

      if (env?.type === 'change') {
        ensure(dayKey).change.push(item);
      } else if (env?.type === 'factor') {
        ensure(dayKey).factor.push(item);
      } else {
        ensure(dayKey).factor.push(item);
      }
    }

    // Sort days
    const sortedKeys = Object.keys(buckets).sort(
      (a, b) =>
        moment(a, 'YYYY-MM-DD').valueOf() - moment(b, 'YYYY-MM-DD').valueOf(),
    );

    if (!sortedKeys.length) {
      setAnalysis({
        data: [],
        envLineData: [],
        phaseChanges: [],
        lines: [],
        maxY: 0,
      });
      return;
    }

    // X-axis: one entry per day
    const data = sortedKeys.map(key => ({
      startTimestamp: moment(key, 'YYYY-MM-DD').startOf('day').valueOf(),
      key,
    }));

    // Daily points (average seconds → minutes for y), duration string for tooltip
    let maxY = 0;
    const dailyPoints = sortedKeys
      .map((key, idx) => {
        const b = buckets[key];
        if (!b.points.length) {
          return null;
        }

        const avgSeconds =
          b.points.reduce((s, p) => s + (Number(p.seconds) || 0), 0) /
          b.points.length;

        const yMin = avgSeconds / 60;
        maxY = Math.max(maxY, yMin);

        const stateCounts = _.countBy(b.points.map(p => p.state || ''));
        const state =
          Object.keys(stateCounts).sort(
            // eslint-disable-next-line @typescript-eslint/no-shadow
            (a, b) => stateCounts[b] - stateCounts[a],
          )[0] || '';

        const collectors = _.uniq(b.points.flatMap(p => p.collectors || []));

        return {
          x: idx + 1,
          y: yMin,
          date: moment(key, 'YYYY-MM-DD').startOf('day').valueOf(),
          duration: durationTime(avgSeconds),
          collectors,
          state,
        };
      })
      .filter(Boolean) as Array<{
      x: number;
      y: number; // minutes
      date: number;
      duration: string;
      collectors: string[];
      state: string;
    }>;

    // Segment by state → lines + phaseChanges
    const lines: any[] = [];
    const phaseChanges: any[] = [];

    if (dailyPoints.length) {
      let currentState = {
        state: dailyPoints[0].state,
        aggregate: dailyPoints[0].x,
        num: 1,
        index: dailyPoints[0].x,
      };
      let latestLine = {
        state: dailyPoints[0].state,
        points: [dailyPoints[0]],
        type: 'point' as const,
      };

      for (let i = 1; i < dailyPoints.length; i++) {
        const p = dailyPoints[i];
        if (p.state === latestLine.state) {
          latestLine.points.push(p);
          currentState = {
            ...currentState,
            aggregate: currentState.aggregate + p.x,
            num: currentState.num + 1,
          };
        } else {
          phaseChanges.push({
            line: currentState.index,
            x: currentState.aggregate / currentState.num,
            state: latestLine.state,
          });
          lines.push(latestLine);

          latestLine = {state: p.state, points: [p], type: 'point'};
          currentState = {
            state: p.state,
            aggregate: p.x,
            num: 1,
            index: p.x,
          };
        }
      }
      lines.push(latestLine);
      phaseChanges.push({
        line: currentState.index,
        x: currentState.aggregate / currentState.num,
        state: currentState.state,
      });
    }

    // Unified env overlay per day
    sortedKeys.forEach((key, idx) => {
      const b = buckets[key];
      const x = idx + 1;
      const envItems = [
        ...b.change.map(item => ({...item, type: 'change'})),
        ...b.factor.map(item => ({...item, type: 'factor'})),
      ];
      if (envItems.length) {
        lines.push({
          x,
          y: 50,
          type: 'env',
          values: {
            [key]: envItems, // 'YYYY-MM-DD'
          },
        });
      }
    });

    setAnalysis({
      data,
      envLineData: [],
      phaseChanges,
      lines,
      maxY,
    });
  };

  useEffect(() => {
    calculateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, envs, program, startDate, endDate, byDay]);

  if (analysis?.data?.length > 0) {
    return (
      <Graph
        yAxisLabel={'Duration (min)'}
        data={analysis.data}
        program={program}
        envs={analysis.envLineData}
        phaseChanges={analysis.phaseChanges}
        lines={analysis.lines}
        maxY={analysis.maxY}
        title={title}
        extras={extras}
        filteredEnvItems={filteredEnvItems}
        shouldShowMenu={shouldShowMenu}
      />
    );
  }

  return (
    <View style={styles.emptyContainer}>
      <Icon name="chart-areaspline" size={30} color={'grey'} />
      <Text style={styles.emptyTitle}>No data</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  emptyContainer: {
    flex: 1,
    minHeight: 100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    ...Typography.P3,
    color: Colors.TEXT_PRIMARY,
    marginTop: 8,
  },
});

export default React.memo(DurationProgressGraph);
